// `StageTypst` (SuiteCompile/StagePlay.swift) and `StageUKTypst` (StagePlayOutputs.swift): Typst source for a play. A US
// play places every line on the page grid `stageLayout` worked out, after a title page, the cast of characters and the
// setting; a UK play is set in Typst's own flow (a proportional face can't sit on a grid of columns): each name in bold
// at the margin with its speech beside it from 1.5 in, directions in italics, pages numbered from the play's first.
import { bylineName, contactName, isPenName, pageInches } from '../manuscript.ts';
import { escape, escapeString } from '../typst/writer.ts';
import type { AuthorInfo, CompiledManuscript } from '../types.ts';
import { count, LEFT_MARGIN, length, run, speaker, TOP_MARGIN, WIDTH, wrap, type Paragraph, type Run, type ScriptLine } from './script.ts';
import {
  continuations, emptyStageScript, numbersPage, printedStageScript, printedStageScriptAsync, stageLayout, stagePageNumber,
  type StagePart, type StageScript,
} from './stage.ts';
import { markup, screenplayFontFamilies, titleLines } from './typst.ts';
import { fountainParagraphs, fountainParagraphText, type Song } from '@writing-suite/core';

const COLUMN = 7.2;
const LINE = 12.0;
const num = (d: number) => d.toFixed(2);

// MARK: US

/** `StageTypst.source`. `fauxBold`: as `screenplayTypst`'s. */
export function stageTypst(m: CompiledManuscript, o: { fauxBold?: boolean } = {}): string {
  const settings = m.preset.render.screenplay;
  const script = printedStageScript(m);
  const size = pageInches(m.preset.render.page.size);
  const layout = stageLayout(script, size.height);
  const width = size.width * 72;
  const left = LEFT_MARGIN * 72, top = TOP_MARGIN * 72;
  const fonts = screenplayFontFamilies(m);
  let out = `#set document(title: "${escapeString(m.title)}"${m.author ? `, author: "${escapeString(bylineName(m.author))}"` : ''})
#set page(width: ${num(size.width * 72)}pt, height: ${num(size.height * 72)}pt, margin: 0pt)
#set text(font: (${fonts.map((f) => `"${escapeString(f)}"`).join(', ')}), size: ${num(m.preset.render.font.size)}pt, lang: "${[...m.language].slice(0, 2).join('')}", hyphenate: false, ligatures: false)
#set smartquote(enabled: false)
`;
  const faux = o.fauxBold ?? false;
  const place = (x: number, y: number, runs: Run[]) => `#place(top + left, dx: ${num(x)}pt, dy: ${num(y)}pt)[${markup(runs, false, faux)}]\n`;
  const centred = (runs: Run[], row: number) => place((width - length(runs) * COLUMN) / 2, top + row * LINE, runs);
  const plain = (s: string) => [run(s)];
  const page = (lines: ScriptLine[]) => '#page[\n' + lines.map((l) => place(left + l.column * COLUMN, top + l.row * LINE, l.runs)).join('') + ']\n';

  // The title page: the writer's lines if they've written the Title Page, else the title, what it is, and by whom.
  if (m.preset.render.titlePage.include) {
    let body = '';
    let row = 20;
    const field = (key: string) => script.titlePage.find((f) => f.key === key)?.value;
    if (script.titleLines !== null && field('title') === undefined) {
      script.titleLines.forEach((text, i) => {
        for (const l of wrap([run(i === 0 ? text.toUpperCase() : text)], WIDTH)) { body += centred(l, row); row += 1; }
        row += 1;
      });
    } else {
      for (const l of titleLines(field('title') ?? m.title, true)) { body += centred(l, row); row += 1; }
      row += 1;
      for (const l of titleLines(field('credit') ?? script.description)) { body += centred(l, row); row += 1; }
      const author = field('author') ?? field('authors') ?? (m.preset.render.titlePage.byline && m.author ? bylineName(m.author) : undefined);
      if (field('author') === undefined && field('authors') === undefined && m.preset.render.titlePage.byline && script.credits !== null) {
        // A musical's credits by role, each its own line.
        row += 1;
        for (const credit of script.credits) {
          row += 1;
          for (const l of titleLines(credit)) { body += centred(l, row); row += 1; }
        }
      } else if (author !== undefined) {
        row += 1;
        body += centred(plain('by'), row);
        row += 2;
        for (const l of titleLines(author)) { body += centred(l, row); row += 1; }
      }
    }
    const bottom = layout.linesPerPage - 1;
    let contact = field('contact');
    if (contact === undefined && m.preset.render.titlePage.contact && m.author && m.author.contact.length) {
      contact = [...(isPenName(m.author) ? [contactName(m.author)] : []), ...m.author.contact].join('\n');
    }
    const lines = titleLines(contact ?? '');
    lines.forEach((l, i) => { body += place(left, top + (bottom - lines.length + 1 + i) * LINE, l); });
    const corner = [field('draft date'), field('date'), field('copyright'), field('notes')]
      .filter((v): v is string => v !== undefined).flatMap((v) => titleLines(v));
    corner.forEach((l, i) => { body += place(left + (WIDTH - length(l)) * COLUMN, top + (bottom - corner.length + 1 + i) * LINE, l); });
    out += `#page[\n${body}]\n`;
  }

  // The cast of characters, then the setting: each a page (or more) of its own, unnumbered.
  for (const lines of frontPages(script, layout.linesPerPage)) out += page(lines);

  // The play: every page numbered at the top right.
  layout.pages.forEach((scriptPage, n) => {
    const part = n < layout.pageParts.length ? layout.pageParts[n] : 0;
    const number = stagePageNumber(script, n + 1, part, settings?.pageNumbers);
    let body = place(left + (WIDTH - count(number)) * COLUMN, top / 2, plain(number));
    for (const l of scriptPage.lines) body += place(left + l.column * COLUMN, top + l.row * LINE, l.runs);
    out += `#page[\n${body}]\n`;
  });
  return out;
}

/** `StageTypst.frontPages`: the cast and setting pages as lines on the grid: a heading centred over the text, then its
 *  entries. */
export function frontPages(script: StageScript, linesPerPage: number): ScriptLine[][] {
  const width = WIDTH;
  const line = (row: number, column: number, runs: Run[]): ScriptLine => ({ row, column, runs, isSceneHeading: false });
  type Piece = { column: number; runs: Run[] };
  const wrapped = (text: string, column = 0): Piece[] => wrap([run(text)], width - column).map((runs) => ({ column, runs }));
  const centred = (text: string): Piece => ({ column: Math.max(0, Math.trunc((width - count(text)) / 2)), runs: [run(text)] });
  const pages: ScriptLine[][] = [];
  const paginate = (heading: string, blocks: Piece[][]) => {
    // `ScriptLayout.Paginator` with `place` and `newPage` only.
    const out: ScriptLine[][] = [[]];
    let row = 0;
    const room = (gap: number) => linesPerPage - row - (row > 0 ? gap : 0);
    const newPage = () => { if (row > 0 || out[out.length - 1].length) out.push([]); row = 0; };
    const place = (list: Piece[], gap: number) => {
      if (row > 0) row += gap;
      for (const p of list) {
        if (row >= linesPerPage) newPage();
        out[out.length - 1].push(line(row, p.column, p.runs));
        row += 1;
      }
    };
    place([centred(heading)], 0);
    blocks.forEach((block, i) => {
      const gap = i === 0 ? 2 : 1;
      if (block.length > room(gap) && block.length <= linesPerPage) newPage();
      place(block, gap);
    });
    if (out.length > 1 && out[out.length - 1].length === 0) out.pop();
    pages.push(...out);
  };
  // Names at the margin, each description at column 25 beside it (under it when the name runs that far).
  const column = 25;
  if (script.cast.length || script.castNotes.length) {
    const entries = script.cast.map((entry): Piece[] => {
      if (entry.description === '') return wrapped(entry.name);
      const text = wrapped(entry.description, column);
      if (count(entry.name) >= column - 1) return [...wrapped(entry.name), ...text];
      const name = [...entry.name];
      text[0] = { column: 0, runs: [run(name.length >= column ? name.slice(0, column).join('') : entry.name + ' '.repeat(column - name.length)), ...text[0].runs] };
      return text;
    });
    paginate('CHARACTERS', [...entries, ...script.castNotes.map((n) => wrapped(n))]);
  }
  // A musical's numbers: the writer's page as written, else each act's songs with who sings them beside.
  if (script.musicalNumbers !== null) {
    paginate('MUSICAL NUMBERS', script.musicalNumbers.map((t) => wrapped(t)));
  } else if (script.songs.length) {
    const blocks: Piece[][] = [];
    for (const group of numbersPage(script)) {
      if (group.act !== null) blocks.push([centred(group.act)]);
      for (const song of group.songs) blocks.push(numberEntry(song, width));
    }
    paginate('MUSICAL NUMBERS', blocks);
  }
  if (script.setting.length) {
    // A line such as "Time" or "Place" heads what follows it, in capitals at the centre.
    paginate('SETTING', script.setting.map((text) =>
      (count(text) <= 24 && !/[.,;!?]/.test(text) ? [centred(text.toUpperCase())] : wrapped(text))));
  }
  return pages;
}

/** `StageTypst.numberEntry`: a song on the Musical Numbers page: "1. The River's High" at the margin, who sings it from
 *  column 35 (under it when the title runs that far). */
export function numberEntry(song: Song, width: number): { column: number; runs: Run[] }[] {
  const column = 35;
  const title = wrap([run(`${song.number}. ${song.title}`)], column - 2).map((runs) => ({ column: 0, runs }));
  if (!song.singers.length) return title;
  const singers = wrap([run(song.singers.join(', '))], width - column).map((runs) => ({ column, runs }));
  if (title.length !== 1) return [...title, ...singers];
  return [{ column: 0, runs: [...title[0].runs, run(' '.repeat(Math.max(1, column - length(title[0].runs)))), ...singers[0].runs] }, ...singers.slice(1)];
}

/** `StageTypst.pageCount`: the pages a US play runs, the pages before it aside. */
export function stagePageCountOf(m: CompiledManuscript): number {
  return stageLayout(printedStageScript(m), pageInches(m.preset.render.page.size).height).pages.length;
}

// MARK: UK

/** `StageUKTypst.Setup`: what the source needs beyond the script. */
export interface StageUKSetup {
  width: number; height: number; margin: number;
  font: string;
  /** Faces to fall back on where `font` isn't installed (a radio play's: the free faces metric-compatible with Arial). */
  fallbacks: string[];
  size: number; title: string; language: string;
  author: AuthorInfo | null;
  titlePage: boolean; byline: boolean; contact: boolean;
}

const DEFAULT_SETUP: StageUKSetup = {
  width: 595.28, height: 841.89, margin: 72, font: 'Times New Roman', fallbacks: ['Times New Roman', 'Libertinus Serif'], size: 12, title: '', language: 'en', author: null,
  titlePage: true, byline: true, contact: true,
};

export function stageUKSetup(m: CompiledManuscript): StageUKSetup {
  const r = m.preset.render;
  const size = pageInches(r.page.size);
  return {
    width: size.width * 72, height: size.height * 72, margin: r.page.margins * 72,
    font: r.font.family, fallbacks: ['Times New Roman', 'Libertinus Serif'], size: r.font.size, title: m.title, language: [...m.language].slice(0, 2).join(''), author: m.author,
    titlePage: r.titlePage.include, byline: r.titlePage.byline, contact: r.titlePage.contact,
  };
}

/** The fonts a UK play's source names (for loading fonts). */
export function stageUKFontFamilies(m: CompiledManuscript): string[] {
  return [m.preset.render.font.family, 'Times New Roman', 'Libertinus Serif'].filter((f, i, all) => all.indexOf(f) === i);
}

/** `StageUKTypst.source(_ m:)`: the whole play, or a sample cut where `pages` says (by Typst) its pages end. */
export async function stageUKTypst(m: CompiledManuscript, pages?: (source: string) => Promise<number | null>): Promise<string> {
  const setup = stageUKSetup(m);
  const counting = { ...setup, titlePage: false };
  const script = pages
    ? await printedStageScriptAsync(m, (s) => pages(stageUKSource(s, counting)))
    : printedStageScript(m);
  return stageUKSource(script, setup);
}

/** `StageUKTypst.source(_:setup:)`. */
export function stageUKSource(script: StageScript, s: StageUKSetup): string {
  const fonts = [s.font, ...s.fallbacks].filter((f, i, all) => all.indexOf(f) === i);
  let out = `#set document(title: "${escapeString(s.title)}"${s.author ? `, author: "${escapeString(bylineName(s.author))}"` : ''})
#set page(width: ${num(s.width)}pt, height: ${num(s.height)}pt, margin: ${num(s.margin)}pt)
#set text(font: (${fonts.map((f) => `"${escapeString(f)}"`).join(', ')}), size: ${num(s.size)}pt, lang: "${s.language}", hyphenate: false)
#set par(justify: false, leading: 0.55em, spacing: 1.1em)
#set grid(column-gutter: 0pt)
#let speech(name, body) = block(spacing: 1.1em, grid(columns: (1.5in, 1fr), name, body))
`;
  // The title page.
  if (s.titlePage) {
    const field = (key: string) => script.titlePage.find((f) => f.key === key)?.value;
    let lines: string[] = [];
    if (script.titleLines !== null && field('title') === undefined) {
      lines = script.titleLines.map((line, i) => (i === 0 ? `#strong[${escape(line.toUpperCase())}]` : escape(line)));
    } else {
      lines.push(`#strong[${escape((field('title') ?? s.title).toUpperCase())}]`);
      lines.push(escape(field('credit') ?? script.description));
      const author = field('author') ?? field('authors') ?? (s.byline && s.author ? bylineName(s.author) : undefined);
      if (field('author') === undefined && field('authors') === undefined && s.byline && script.credits !== null) lines.push(...script.credits.map((c) => escape(c)));
      else if (author !== undefined) lines.push('by', escape(author));
    }
    let contact = field('contact');
    if (contact === undefined && s.contact && s.author && s.author.contact.length) {
      contact = [...(isPenName(s.author) ? [contactName(s.author)] : []), ...s.author.contact].join('\n');
    }
    const contactBlock = (contact ?? '').split('\n').filter((l) => l !== '').map((l) => escape(l)).join(' \\\n');
    out += `#page[\n#v(3in)\n#align(center)[\n${lines.join('\n\n')}\n]\n`;
    if (contactBlock !== '') out += `#place(bottom + left)[${contactBlock}]\n`;
    out += ']\n';
  }
  // The cast, then the setting: pages of their own, unnumbered.
  if (script.cast.length || script.castNotes.length) {
    const rows = script.cast.map((c) => `[#strong[${escape(c.name)}]], [${escape(c.description)}]`).join(',\n');
    out += '#page[\n#align(center)[#strong[CHARACTERS]]\n#v(1em)\n';
    if (rows !== '') out += `#grid(columns: (2.5in, 1fr), row-gutter: 1em,\n${rows})\n`;
    for (const note of script.castNotes) out += `\n${escape(note)}\n`;
    out += ']\n';
  }
  if (script.musicalNumbers !== null) {
    out += '#page[\n#align(center)[#strong[MUSICAL NUMBERS]]\n#v(1em)\n';
    for (const line of script.musicalNumbers) out += `\n${escape(line)}\n`;
    out += ']\n';
  } else if (script.songs.length) {
    out += '#page[\n#align(center)[#strong[MUSICAL NUMBERS]]\n#v(1em)\n';
    for (const group of numbersPage(script)) {
      if (group.act !== null) out += `#align(center)[#strong[${escape(group.act)}]]\n#v(0.5em)\n`;
      const rows = group.songs.map((s) => `[${escape(`${s.number}. ${s.title}`)}], [${escape(s.singers.join(', '))}]`);
      out += `#grid(columns: (3in, 1fr), row-gutter: 1em,\n${rows.join(',\n')})\n#v(1em)\n`;
    }
    out += ']\n';
  }
  if (script.setting.length) {
    out += '#page[\n#align(center)[#strong[SETTING]]\n#v(1em)\n';
    for (const text of script.setting) {
      out += count(text) <= 24 && !/[.,;!?]/.test(text) ? `\n#align(center)[#strong[${escape(text.toUpperCase())}]]\n` : `\n${escape(text)}\n`;
    }
    out += ']\n';
  }
  // The play, numbered from its first page.
  if (!script.parts.length) return out;
  // Counted from the page the play starts on (a counter reset there comes after that page's header is drawn).
  out += '#set page(header: context align(right)[#str(here().page() - locate(<play-start>).page() + 1)])\n#metadata(none) <play-start>\n';
  script.parts.forEach((part, index) => {
    if (index > 0) out += '#pagebreak(weak: true)\n';
    if (part.headings.length) {
      const cells = part.headings.map((h) => `[#strong[${escape(h)}]]`);
      out += cells.length === 1 ? `#block(spacing: 1.6em)${cells[0]}\n` : `#block(spacing: 1.6em, grid(columns: (1.5in, 1fr), ${cells.join(', ')}))\n`;
    }
    out += ukBody(part.paragraphs, script.musical);
    if (part.end !== null) out += `\n${escape(part.end)}\n`;
  });
  return out;
}

/** `StageUKTypst.body`: the scene's elements as Typst. */
function ukBody(paragraphs: readonly Paragraph[], musical = false): string {
  let out = '';
  const continues = continuations(paragraphs);
  const lines = (p: Paragraph) => p.lines.map(ukMarkup).join(' \\\n');
  let i = 0;
  while (i < paragraphs.length) {
    const p = paragraphs[i];
    switch (p.element) {
      case 'character': {
        // The speech: its dialogue, parentheticals (italic, in the run of the speech) and lyrics (italic).
        let j = i + 1;
        const parts: string[] = [];
        while (j < paragraphs.length && ['dialogue', 'parenthetical', 'lyric'].includes(paragraphs[j].element)) {
          const q = paragraphs[j];
          // A musical's lyrics are in capitals, upright.
          parts.push(q.element === 'dialogue' || (q.element === 'lyric' && musical) ? lines(q) : `#emph[${lines(q)}]`);
          j += 1;
        }
        const name = speaker(fountainParagraphText(p)).toUpperCase();
        const cue = continues[i] ? '[]' : `[#strong[${escape(name)}:]]`;
        out += `#speech(${cue}, [${parts.join(' ')}])\n`;
        i = j;
        continue;
      }
      case 'dialogue': case 'parenthetical': case 'lyric': out += `#speech([], [${lines(p)}])\n`; break;
      case 'action': out += `\n#emph[${lines(p)}]\n`; break;
      case 'centered': out += `\n#align(center)[${lines(p)}]\n`; break;
      case 'pageBreak': out += '#pagebreak(weak: true)\n'; break;
      case 'sceneHeading': out += `\n#strong[${lines(p)}]\n`; break;
      default: out += `\n${lines(p)}\n`;
    }
    i += 1;
  }
  return out;
}

/** `StageUKTypst.markup`: styled text in Typst's flow, emphasis as written, spaces as they are. */
export function stageUKMarkup(runs: Run[]): string { return ukMarkup(runs); }

function ukMarkup(runs: Run[]): string {
  return runs.map((r) => {
    let s = escape(r.text);
    if (r.italic) s = `#emph[${s}]`;
    if (r.bold) s = `#strong[${s}]`;
    if (r.underline) s = `#underline[${s}]`;
    return s;
  }).join('');
}

/** `StageUKTypst.pageCount(fountain:engine:)`: the pages a UK play's scenes print on (Write's footer), counted by Typst. */
export async function stageUKPageCount(texts: readonly string[], pages: (source: string) => Promise<number | null>): Promise<number | null> {
  const parts = texts.filter((t) => !/^\p{White_Space}*$/u.test(t)).map((t): StagePart =>
    ({ act: null, scene: null, headings: [], paragraphs: fountainParagraphs(t), end: null }));
  if (!parts.length) return 0;
  return pages(stageUKSource({ ...emptyStageScript(), parts }, { ...DEFAULT_SETUP, titlePage: false }));
}

